import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { ArrowLeft } from "lucide-react";
import { trackInitiateCheckout, trackPurchase, trackSchedule } from "./utils/tracking";
import { API, toISODate, loadRazorpayScript } from "./components/consultation/constants";
import ProgressSteps from "./components/consultation/ProgressSteps";
import StepTypeSelector from "./components/consultation/StepTypeSelector";
import { CalendarPicker, TimeSlotPicker } from "./components/consultation/CalendarPicker";
import ClientDetailsForm from "./components/consultation/ClientDetailsForm";
import ReviewSummary from "./components/consultation/ReviewSummary";
import SuccessView from "./components/consultation/SuccessView";
import ConsultationTestimonials from "./components/consultation/ConsultationTestimonials";

export default function ConsultationPage() {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [selectedType, setSelectedType] = useState(null);
  const [formData, setFormData] = useState({ name: "", email: "", phone: "", alt_phone: "", health_concern: "" });
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedSlot, setSelectedSlot] = useState(null);
  const [currentMonth, setCurrentMonth] = useState(new Date().getMonth());
  const [currentYear, setCurrentYear] = useState(new Date().getFullYear());
  const [isProcessing, setIsProcessing] = useState(false);
  const [paymentStatus, setPaymentStatus] = useState(null);
  const [razorpayKey, setRazorpayKey] = useState(null);

  useEffect(() => {
    let cancelled = false;
    axios.get(`${API}/razorpay-key`)
      .then((res) => { if (!cancelled && res.data.configured) setRazorpayKey(res.data.key_id); })
      .catch(() => setPaymentStatus({ type: "error", message: "Payment gateway unavailable. Please try later." }));
    return () => { cancelled = true; };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps -- mount-only fetch

  const handleDateSelect = (day) => { setSelectedDate(new Date(currentYear, currentMonth, day)); setSelectedSlot(null); };
  const handlePrevMonth = () => { if (currentMonth === 0) { setCurrentMonth(11); setCurrentYear(currentYear - 1); } else setCurrentMonth(currentMonth - 1); };
  const handleNextMonth = () => { if (currentMonth === 11) { setCurrentMonth(0); setCurrentYear(currentYear + 1); } else setCurrentMonth(currentMonth + 1); };
  const canGoPrev = currentYear > new Date().getFullYear() || (currentYear === new Date().getFullYear() && currentMonth > new Date().getMonth());
  const isFormValid = formData.name && formData.phone && formData.health_concern;
  const isScheduleValid = selectedDate && selectedSlot;

  const handlePayment = async () => {
    if (!razorpayKey) { setPaymentStatus({ type: "error", message: "Payment not configured." }); return; }
    setIsProcessing(true);
    try {
      const loaded = await loadRazorpayScript();
      if (!loaded) throw new Error("Failed to load payment gateway");
      const orderRes = await axios.post(`${API}/create-order`, {
        amount: selectedType.price * 100, program_name: `Consultation - ${selectedType.title}`,
        customer_name: formData.name, customer_email: formData.email || null,
        customer_phone: formData.phone, consultation_date: toISODate(selectedDate),
        consultation_slot: selectedSlot, health_concern: formData.health_concern, alt_phone: formData.alt_phone || null
      });
      const options = {
        key: razorpayKey, amount: orderRes.data.amount, currency: orderRes.data.currency,
        name: "Fitly India", description: `${selectedType.title} (${selectedType.duration})`, order_id: orderRes.data.order_id,
        handler: async (response) => {
          setPaymentStatus({ type: "success", message: "Payment successful!" });
          setStep(5); setIsProcessing(false);
          const verifyData = { razorpay_order_id: response.razorpay_order_id, razorpay_payment_id: response.razorpay_payment_id, razorpay_signature: response.razorpay_signature };
          for (let attempt = 0; attempt < 3; attempt++) {
            try { await axios.post(`${API}/verify-payment`, verifyData); break; }
            catch (err) { if (attempt < 2) await new Promise(r => setTimeout(r, 2000)); }
          }
          try { trackPurchase({ value: selectedType.price, currency: 'INR', content_name: selectedType?.title }); } catch (e) { console.warn('Analytics tracking failed:', e.message); }
        },
        prefill: { name: formData.name, email: formData.email, contact: formData.phone },
        theme: { color: "#132A13" },
        modal: { ondismiss: () => setIsProcessing(false) }
      };
      const rzp = new window.Razorpay(options);
      rzp.on("payment.failed", () => { setPaymentStatus({ type: "error", message: "Payment failed. Please try again." }); setIsProcessing(false); });
      rzp.open();
    } catch {
      setPaymentStatus({ type: "error", message: "Could not initiate payment. Please try again." });
    } finally {
      setIsProcessing(false);
    }
  };

  const NavButtons = ({ onBack, onNext, nextDisabled, nextLabel, backTestId, nextTestId }) => (
    <div className="flex justify-between mt-8">
      <button onClick={onBack} data-testid={backTestId}
        className="border border-border_custom text-emerald_deep px-8 py-3 text-sm tracking-wide hover:bg-bone transition-all rounded-full">Back</button>
      <button onClick={onNext} disabled={nextDisabled} data-testid={nextTestId}
        className={`px-8 py-3 text-sm tracking-wide transition-all rounded-full ${
          nextDisabled ? "bg-border_custom text-text_muted/50 cursor-not-allowed" : "bg-emerald_deep text-bone hover:bg-emerald_deep/90"
        }`}>{nextLabel}</button>
    </div>
  );

  return (
    <div className="min-h-screen bg-bone text-text_main">
      <header className="border-b border-border_custom bg-bone/95 backdrop-blur-md sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 py-4 flex items-center justify-between">
          <button onClick={() => navigate("/")} className="flex items-center gap-2 text-text_muted hover:text-emerald_deep transition-colors" data-testid="back-to-home">
            <ArrowLeft size={18} /><span className="text-sm tracking-wide">Back to Home</span>
          </button>
          <a href="/" className="font-serif text-xl" data-testid="consultation-logo">
            <span className="font-semibold text-emerald_deep">Fitly</span> <span className="font-light text-text_muted">India</span>
          </a>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-6 lg:px-8 py-12">
        <div className="text-center mb-12">
          <span className="text-xs tracking-[0.2em] uppercase font-semibold text-accent">Personalized Guidance</span>
          <h1 className="font-serif text-3xl sm:text-4xl lg:text-5xl tracking-tight text-emerald_deep mt-3 mb-4" data-testid="consultation-title">Book Your Consultation</h1>
          <p className="text-text_muted max-w-xl mx-auto">One-on-one consultation with Nadiya for personalized nutrition and wellness guidance</p>
        </div>

        {step <= 4 && <ProgressSteps step={step} />}

        {step === 1 && <StepTypeSelector onSelect={(type) => { setSelectedType(type); setStep(2); }} />}

        {step === 2 && (
          <div data-testid="step-2-details">
            <h2 className="font-serif text-2xl text-center text-emerald_deep mb-8">Your Details</h2>
            <ClientDetailsForm formData={formData} setFormData={setFormData} />
            <NavButtons onBack={() => setStep(1)} onNext={() => { if (isFormValid) { setStep(3); trackSchedule({ content_name: selectedType?.title }); } }}
              nextDisabled={!isFormValid} nextLabel="Choose Preferred Slot" backTestId="step2-back" nextTestId="step2-next" />
          </div>
        )}

        {step === 3 && (
          <div data-testid="step-3-schedule">
            <h2 className="font-serif text-2xl text-center text-emerald_deep mb-8">Select Preferred Slot</h2>
            <p className="text-center text-sm text-text_muted mb-8">Choose your preferred date and time. Our team will confirm availability.</p>
            <CalendarPicker currentMonth={currentMonth} currentYear={currentYear} selectedDate={selectedDate}
              onDateSelect={handleDateSelect} onPrevMonth={handlePrevMonth} onNextMonth={handleNextMonth} canGoPrev={canGoPrev} />
            {selectedDate && <TimeSlotPicker selectedDate={selectedDate} selectedSlot={selectedSlot} onSlotSelect={setSelectedSlot} />}
            <NavButtons onBack={() => setStep(2)} onNext={() => { if (isScheduleValid) { setStep(4); try { trackInitiateCheckout({ value: selectedType.price, currency: 'INR', content_name: selectedType?.title }); } catch (e) { console.warn('Analytics tracking failed:', e.message); } } }}
              nextDisabled={!isScheduleValid} nextLabel="Continue to Payment" backTestId="step3-back" nextTestId="step3-next" />
          </div>
        )}

        {step === 4 && (
          <div data-testid="step-4-payment">
            <h2 className="font-serif text-2xl text-center text-emerald_deep mb-8">Review &amp; Pay</h2>
            <ReviewSummary selectedType={selectedType} formData={formData} selectedDate={selectedDate} selectedSlot={selectedSlot} />
            {paymentStatus && (
              <div className={`p-4 mb-6 border rounded-lg text-sm ${paymentStatus.type === "error" ? "border-red-200 bg-red-50 text-red-600" : "border-emerald_deep/20 bg-emerald_deep/5 text-emerald_deep"}`}>
                {paymentStatus.message}
              </div>
            )}
            <div className="flex justify-between mt-8">
              <button onClick={() => setStep(3)} data-testid="step4-back"
                className="border border-border_custom text-emerald_deep px-8 py-3 text-sm tracking-wide hover:bg-bone transition-all rounded-full">Back</button>
              <button onClick={handlePayment} disabled={isProcessing} data-testid="pay-now-btn"
                className="bg-emerald_deep text-bone px-10 py-3 text-sm tracking-wide hover:bg-emerald_deep/90 transition-all disabled:opacity-50 rounded-full">
                {isProcessing ? "Processing..." : `Pay \u20B9${(selectedType?.price + Math.round(selectedType?.price * 0.18)).toLocaleString()}`}
              </button>
            </div>
          </div>
        )}

        {step === 5 && <SuccessView selectedType={selectedType} selectedDate={selectedDate} selectedSlot={selectedSlot} navigate={navigate} />}
        {step < 5 && <ConsultationTestimonials />}
      </main>
    </div>
  );
}
